import React, { useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import { Pie, Bar } from "react-chartjs-2";
import { toast } from "react-toastify";

import { enums } from "../../constants/enum";
import useReportService from "../../services/api/reportService";

import reportSchema from "../../schemas/report";
import { Badge, Button, Chip, Loading, PageTitle } from "../../components";
import { DateInput, SelectInput } from "../../components/forms";
import { SearchIcon } from "../../assets/icons";

import "./ReportsPage.scss";

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
);

function ReportsPage() {
  const {
    getRevenueReport,
    getOrderStatusReport,
    recentCampaigns,
    salesReport,
    stockReport,
    queryKey,
  } = useReportService();

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    range: enums.REPORTING_PERIODS.DAILY,
  });

  const [
    revenueReportApi,
    orderStatusReportApi,
    recentCampaignsApi,
    salesReportApi,
    stockReportApi,
  ] = useQueries({
    queries: [
      {
        queryKey: [queryKey, "revenue-report", filters],
        queryFn: () =>
          getRevenueReport(filters.range, filters.from, filters.to),
      },
      {
        queryKey: [queryKey, "order-status-report", filters],
        queryFn: () =>
          getOrderStatusReport(filters.range, filters.from, filters.to),
      },
      {
        queryKey: [queryKey, "recent-campaigns"], // Campaign list might not be filtered by date/range based on service (check service signature)
        // Service signature: recentCampaigns() - takes no args. OK.
        queryFn: () => recentCampaigns(),
      },
      {
        queryKey: [queryKey, "sales-report", filters],
        queryFn: () => salesReport(filters.range, filters.from, filters.to),
      },
      {
        queryKey: [queryKey, "stock-report", filters],
        queryFn: () => stockReport(filters.range, filters.from, filters.to),
      },
    ],
  });
  const loading =
    revenueReportApi.isLoading ||
    orderStatusReportApi.isLoading ||
    recentCampaignsApi.isLoading ||
    salesReportApi.isLoading ||
    stockReportApi.isLoading;
  const error =
    revenueReportApi.error ||
    orderStatusReportApi.error ||
    recentCampaignsApi.error ||
    salesReportApi.error ||
    stockReportApi.error;

  if (loading) {
    return <Loading variant="fullScreen" />;
  }

  if (error) {
    toast.error("Error loading reports");
  }
  const revenueReportData = revenueReportApi.data;
  const orderStatusReportData = orderStatusReportApi.data;
  const recentCampaignsReportData = recentCampaignsApi.data;
  const salesReportData = salesReportApi.data;
  const stockReportData = stockReportApi.data;


  // Total Revenue Pie Chart
  const revenueData = {
    labels: ["Cash", "Card", "Other"],
    datasets: [
      {
        data: [
          revenueReportData?.report?.cashPercentage || 0,
          revenueReportData?.report?.cardPercentage || 0,
          (revenueReportData?.report?.bankTransferPercentage || 0) +
            (revenueReportData?.report?.chequePercentage || 0),
        ],
        backgroundColor: [
          "#606060", // Cash (Dark Grey)
          "#AAAAAA", // Card (Medium Grey)
          "#EEEEEE", // Other (Light Grey)
        ],
        borderWidth: 0,
      },
    ],
  };

  const revenueOptions = {
    plugins: {
      legend: {
        display: false, // Hide default legend
      },
      tooltip: {
        enabled: true,
      },
    },
    cutout: "0%",
    maintainAspectRatio: false,
  };

  // Sales by Item Category Bar Chart
  const salesData = {
    labels: Object.values(enums.ITEM_CATEGORIES),
    datasets: [
      {
        label: "Sales",
        data: Object.values(enums.ITEM_CATEGORIES).map((category) => {
          const item = salesReportData?.report?.find(
            (reportItem) => reportItem.category === category,
          );
          return item?.totalRevenue || 0;
        }),
        backgroundColor: "#4D4D4D",
        barPercentage: 0.4,
        borderRadius: 4,
      },
    ],
  };

  const salesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "#f0f0f0",
        },
        border: {
          display: false,
        },
        ticks: {
          stepSize: 5000,
          callback: (value) => (value >= 1000 ? value / 1000 + "k" : value),
          color: "#a0a0a0",
          font: { size: 10 },
        },
      },
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#a0a0a0",
          font: { size: 10 },
        },
      },
    },
  };

  // Stock Availability Bar Chart (Grouped)
  const stockData = {
    labels: Object.values(enums.ITEM_CATEGORIES),
    datasets: [
      {
        label: "Showroom Stock",
        data: Object.values(enums.ITEM_CATEGORIES).map((category) => {
          const item = stockReportData?.report?.find(
            (reportItem) => reportItem.category === category,
          );
          return item?.displayStock || 0;
        }),
        backgroundColor: "#4D4D4D",
        barPercentage: 0.9,
        categoryPercentage: 0.5,
      },
      {
        label: "Storeroom Stock",
        data: Object.values(enums.ITEM_CATEGORIES).map((category) => {
          const item = stockReportData?.report?.find(
            (reportItem) => reportItem.category === category,
          );
          return item?.storeRoomStock || 0;
        }),
        backgroundColor: "#AAAAAA",
        barPercentage: 0.9,
        categoryPercentage: 0.5,
      },
    ],
  };

  const stockOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "start",
        labels: {
          boxWidth: 12,
          usePointStyle: false,
          color: "#000",
        },
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "#f0f0f0",
        },
        border: {
          display: false,
        },
        ticks: {
          stepSize: 100,
          callback: (value) => (value > 0 ? value / 1000 + "k" : value),
          color: "#a0a0a0",
          font: { size: 10 },
        },
      },
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#a0a0a0",
          font: { size: 10 },
        },
      },
    },
  };

  const handleFilterSubmit = (values) => {
    setFilters(values);
  };

  return (
    <div className="page reports-page">
      <div className="header-controls">
        <PageTitle text="Reports" />
        <Formik
          initialValues={filters}
          onSubmit={handleFilterSubmit}
          enableReinitialize
          validationSchema={reportSchema}
        >
          {({ values }) => (
            <Form>
              {values.range === enums.REPORTING_PERIODS.CUSTOM && (
                <>
                  <div className="date-filter">
                    <DateInput name="from" label="From" />
                  </div>
                  <div className="date-filter">
                    <DateInput name="to" label="To" />
                  </div>
                </>
              )}
              <div className="range-filter" style={{ minWidth: "150px" }}>
                <SelectInput
                  name="range"
                  values={Object.values(enums.REPORTING_PERIODS).map(
                    (period) => ({
                      label: enums.REPORTING_PERIODS_LABELS[period],
                      value: period,
                    }),
                  )}
                />
              </div>
              <Button
                icon={<SearchIcon />}
                type="submit"
                className="filter-btn"
              />
            </Form>
          )}
        </Formik>
      </div>

      <div className="charts-grid">
        {/* Row 1: Revenue & Sales */}
        <div className="row-1">
          {/* Pie Chart */}
          <div className="card revenue-card">
            <h3>Total Revenue</h3>
            <div className="chart-container">
              <div className="pie-wrapper">
                <Pie data={revenueData} options={revenueOptions} />
              </div>
            </div>

            {/* Custom Legend */}
            <div className="custom-legend">
              <div className="legend-item">
                <div
                  className="dot"
                  style={{ backgroundColor: "#606060" }}
                ></div>
                <div className="info">
                  <span className="label">Cash</span>
                  <span className="value">
                    {Math.round(revenueReportData?.report?.cashPercentage || 0)}
                    %
                  </span>
                </div>
              </div>
              <div className="divider"></div>
              <div className="legend-item">
                <div
                  className="dot"
                  style={{ backgroundColor: "#AAAAAA" }}
                ></div>
                <div className="info">
                  <span className="label">Card</span>
                  <span className="value">
                    {Math.round(revenueReportData?.report?.cardPercentage || 0)}
                    %
                  </span>
                </div>
              </div>
              <div className="divider"></div>
              <div className="legend-item">
                <div
                  className="dot"
                  style={{ backgroundColor: "#EEEEEE" }}
                ></div>
                <div className="info">
                  <span className="label">Other</span>
                  <span className="value">
                    {Math.round(
                      (revenueReportData?.report?.bankTransferPercentage || 0) +
                        (revenueReportData?.report?.chequePercentage || 0),
                    )}
                    %
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Sales Bar Chart */}
          <div className="card sales-card">
            <h3>Sales by Item Category</h3>
            <div className="chart-container">
              <Bar data={salesData} options={salesOptions} />
            </div>
          </div>
        </div>

        {/* Row 2: Stats & Campaigns */}
        <div className="row-2">
          {/* Stat Cards */}
          <div className="order-counts">
            <div className="count-card">
              <h2>
                {orderStatusReportData.report?.[enums.ORDER_STATUSES.CONFIRMED]}
              </h2>
              <Chip
                text="Confirmed"
                className="status-badge"
                variant="warning"
              />
            </div>
            <div className="count-card">
              <h2>
                {orderStatusReportData.report?.[enums.ORDER_STATUSES.READY]}
              </h2>
              <Chip text="Ready" className="status-badge" />
            </div>
            <div className="count-card">
              <h2>
                {orderStatusReportData.report?.[enums.ORDER_STATUSES.COMPLETED]}
              </h2>
              <Chip
                text="Completed"
                className="status-badge"
                variant="success"
              />
            </div>
            <div className="count-card">
              <h2>
                {orderStatusReportData.report?.[enums.ORDER_STATUSES.CANCELLED]}
              </h2>
              <Chip
                text="Cancelled"
                className="status-badge"
                variant="danger"
              />
            </div>
          </div>

          {/* Recent Campaigns List */}
          <div className="card campaigns-card">
            <h3>Recent Campaigns</h3>
            <div className="campaigns-list">
              {recentCampaignsReportData.report?.map((campaign) => (
                <div className="campaign-item" key={campaign._id}>
                  <p>
                    {campaign.name} - {campaign.place}
                  </p>
                  <Badge text={campaign.orderCount} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 3: Stock Availability */}
        <div className="row-3 card stock-card">
          <h3>Stock Availability</h3>
          <div className="chart-container">
            <Bar data={stockData} options={stockOptions} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportsPage;
